import { useRef, useState } from 'react'
import { flushSync } from 'react-dom'

import { Play, X } from 'lucide-react'

import { track } from '@/api/tracking'
import { LandingDemoPlayer } from '@/features/demos/LandingDemoPlayer'
import type { LandingDemoId } from '@/features/demos/catalog'
import {
    pauseVideosOutsideContainer,
    resetAndPauseVideos,
    resetAndPlayVideosInContainer,
} from '@/features/demos/demoVideoControls'
import { cn } from '@/lib/utils'
import type { LandingPage } from '@tipi/shared'

type LandingDemoModalTriggerProps = {
    demoId: LandingDemoId
    landingPage: LandingPage
    trackEvent: string
    ctaLabel: string
    title: string
    closeLabel: string
    className?: string
    buttonClassName?: string
    dialogClassName?: string
}

export function LandingDemoModalTrigger({
    demoId,
    landingPage,
    trackEvent,
    ctaLabel,
    title,
    closeLabel,
    className,
    buttonClassName,
    dialogClassName,
}: LandingDemoModalTriggerProps) {
    const wrapRef = useRef<HTMLDivElement>(null)
    const dialogRef = useRef<HTMLDialogElement>(null)
    const [playerMounted, setPlayerMounted] = useState(false)
    const titleId = `${demoId}-demo-modal-title`

    const openDemo = () => {
        const dialog = dialogRef.current
        if (!dialog) return

        track(trackEvent, { landing_page: landingPage })

        if (!playerMounted) {
            flushSync(() => setPlayerMounted(true))
        }

        const section = wrapRef.current?.closest('section')
        if (section) {
            pauseVideosOutsideContainer(section.querySelectorAll('video'), video => dialog.contains(video))
        }

        if (!dialog.open) dialog.showModal()
        resetAndPlayVideosInContainer(dialog)
    }

    const handleClose = () => {
        const dialog = dialogRef.current
        if (dialog) resetAndPauseVideos(dialog.querySelectorAll('video'))
    }

    return (
        <div ref={wrapRef} className={className}>
            <button
                type='button'
                onClick={openDemo}
                className={cn(
                    'inline-flex min-h-11 w-fit items-center gap-2.5 rounded-full border-[1.5px] border-[#F4502A] py-2 pl-2.5 pr-4 text-left text-sm font-semibold leading-snug text-[#F4502A] transition-colors hover:bg-[#F4502A]/10',
                    buttonClassName,
                )}
            >
                <span
                    className='flex size-7 shrink-0 items-center justify-center rounded-full bg-[#F4502A] text-white'
                    aria-hidden
                >
                    <Play className='ml-0.5 size-3.5 fill-current' strokeWidth={0} />
                </span>
                {ctaLabel}
            </button>

            <dialog
                ref={dialogRef}
                aria-labelledby={titleId}
                className={cn(
                    'max-h-[90dvh] w-[min(calc(100%-2rem),380px)] overflow-y-auto rounded-[22px] border-0 bg-transparent p-0 shadow-none backdrop:bg-[#17130E]/70',
                    dialogClassName,
                )}
                onClose={handleClose}
                onClick={event => {
                    if (event.target === event.currentTarget) event.currentTarget.close()
                }}
            >
                <div className='relative rounded-[22px] bg-[#17130E] p-3 pt-12'>
                    <h3 id={titleId} className='sr-only'>
                        {title}
                    </h3>
                    <button
                        type='button'
                        onClick={() => dialogRef.current?.close()}
                        className='absolute right-2 top-2 flex size-11 items-center justify-center rounded-full text-white transition-colors hover:bg-white/10'
                        aria-label={closeLabel}
                    >
                        <X className='size-5' strokeWidth={2.2} aria-hidden />
                    </button>
                    {playerMounted ? <LandingDemoPlayer demoId={demoId} variant='modal' /> : null}
                </div>
            </dialog>
        </div>
    )
}
